import { useRef, useState } from 'react';
import { GiftRequestDTO, useGiftRequests } from '@/hooks/useGiftsRequests';
import TitlePage from '@/components/TitlePage';
import { Column, TableComponent } from '@/components/TableComponent';
import CreateUpdate from './components/createUpdate';
import Delete from './components/delete';
import { Filter, Search } from 'lucide-react';
import LoadingCircle from '@/components/LoadingCircle';
import { Button } from '@/components/ui/button';
import { useProfileContext } from '@/hooks/useProfileContext';
import {
  checkIsPermissionWithScreen,
  checkIsPermisstionApproveHigh,
  checkIsPermisstionApproveLow,
  checkIsRoleEditGiftRequests,
  formatCurrency,
  formatCurrencyWithoutSymbol,
  formatDate,
} from '@/lib/utils';
import { useGifts } from '@/hooks/useGifts';
import StatusBadge from '@/components/StatusBadge';
import { GiftRequestStatus } from '@/api/gifts-requests';
import { Select } from '@/components/ui/select';
import {
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import TextUserInactive from '@/components/TextUserInactive';
import Certificate from './components/certificate';
import ViewCertificate from './components/viewCertificate';
import TextInActive from '@/components/TextInActive';
import { supplierStatus } from '@/api/suppliers';
import { useAllCustomers } from '@/hooks/useCustomer';
import { useGetEmployeeRoleIsSeller } from '@/hooks/useEmployee';
import ApproveButton from './components/approve';
import { Input } from '@/components/ui/input';
import { debounce } from 'lodash';
import DetailGiftRequests from './components/detailGiftRequests';
const limit = 10;

const ManageGiftRequests = () => {
  const { profile } = useProfileContext();
  const isSeller = profile?.role?.key === 'marketing';
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<GiftRequestStatus | null>(null);
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, isFetching } = useGiftRequests({
    page,
    limit,
    employeeId: profile?.role?.key === 'marketing' ? profile?.id : employeeId,
    status,
    customerId,
    search,
  });

  const { data: giftsData, refetch: refetchGifts } = useGifts({
    id: '',
    name: '',
  });

  const { data: customersData } = useAllCustomers();
  const { data: employeesData } = useGetEmployeeRoleIsSeller(isSeller);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingCircle />
      </div>
    );
  }

  const debouncedSearch = debounce((value: string) => {
    setPage(1);
    setSearch(value);
  }, 500);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const renderCertificate = (item: GiftRequestDTO) => {
    switch (item.status) {
      case GiftRequestStatus.COMPLETED:
        return (
          <ViewCertificate
            giftRequestId={item.id}
            assignedToId={item.assignedTo.id}
          />
        );
      case GiftRequestStatus.ADMIN_APPROVED:
      case GiftRequestStatus.GENERAL_DIRECTOR_APPROVED:
      case GiftRequestStatus.APPROVED:
      case GiftRequestStatus.BUSINESS_APPROVED:
        return item?.assignedTo?.id === profile?.id ? (
          <Certificate id={item.id} />
        ) : null;
      default:
        return null;
    }
  };

  const columns: Column<GiftRequestDTO>[] = [
    {
      header: 'Mã yêu cầu',
      accessorKey: 'id',
      cell: (item) => {
        return <DetailGiftRequests giftRequest={item} />;
      },
    },
    {
      header: 'Quà',
      accessorKey: 'gift',
      cell: (item) => {
        return (
          <div className="font-medium text-luxury-900 flex flex-col">
            {item.gift.name}{' '}
            <TextInActive
              status={
                item.gift.supplier.status === supplierStatus.INACTIVE
                  ? item.gift.supplier.status
                  : item.gift.status
              }
            />
          </div>
        );
      },
    },
    {
      header: 'Số lượng quà còn lại',
      accessorKey: 'gift',
      cell: (item) => {
        return (
          <div className="text-right">
            {formatCurrencyWithoutSymbol(item.gift.remainingQuantity)}
          </div>
        );
      },
    },
    {
      header: 'Đơn giá (VNĐ)',
      accessorKey: 'gift',
      cell: (item) => {
        return (
          <div className="text-right">
            {formatCurrency(Number(item.gift.unitPrice))}
          </div>
        );
      },
    },
    {
      header: 'Số lượng quà yêu cầu',
      accessorKey: 'numberOfGiftsPerCustomer',
      headerClassName: 'text-right',
      cell: (item) => {
        return (
          <div className="text-right">
            {formatCurrencyWithoutSymbol(item.numberOfGiftsPerCustomer)} quà x{' '}
            {item.customers.length} khách hàng ={' '}
            {item.numberOfGiftsPerCustomer * item.customers.length} quà
          </div>
        );
      },
    },
    {
      header: 'Tổng giá trị (VNĐ)',
      accessorKey: 'gift',
      headerClassName: 'text-right',
      cell: (item) => {
        return (
          <div className="text-right">
            {formatCurrency(
              item.numberOfGiftsPerCustomer *
                item.gift.unitPrice *
                item.customers.length
            )}
          </div>
        );
      },
    },
    {
      header: 'Khách hàng được tặng',
      accessorKey: 'customers',
      cell: (item) => {
        return (
          <div className="flex flex-wrap gap-3 w-[400px]">
            {item.customers.map((customer) => (
              <div
                className="inline-flex items-center px-2 py-1 text-sm font-medium text-luxury-50 bg-gradient-to-r from-luxury-700 to-luxury-800 rounded-lg border border-luxury-600 shadow-md hover:shadow-lg transition-all duration-200 hover:from-luxury-600 hover:to-luxury-700"
                key={customer.id}
              >
                <div className="w-2 h-2 bg-luxury-300 rounded-full mr-2 opacity-80"></div>
                <span className="font-semibold text-luxury-100">
                  KH-{customer.id}
                </span>
                <span className="mx-2 text-luxury-300">•</span>
                <span className="text-luxury-200">{customer.fullName}</span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      header: 'Lý do',
      accessorKey: 'reason',
      cell: (item) => {
        return (
          <div className="w-[300px] whitespace-pre-line break-words">
            {item.reason}
          </div>
        );
      },
    },
    {
      header: 'Người phụ trách',
      accessorKey: 'assignedTo',
      cell: (item) => {
        return (
          <div className="flex flex-col">
            {item.assignedTo.username}
            <TextUserInactive status={item.assignedTo.status} />
          </div>
        );
      },
    },
    {
      header: 'Người tạo',
      accessorKey: 'createdBy',
      cell: (item) => {
        return (
          <div className="flex flex-col">
            {item.createdBy.username}
            <TextUserInactive status={item.createdBy.status} />
          </div>
        );
      },
    },
    {
      header: 'Ngày tạo',
      accessorKey: 'createdAt',
      cell: (item) => {
        return <div>{formatDate(item.createdAt)}</div>;
      },
    },
    {
      header: 'Ngày cập nhật',
      accessorKey: 'updatedAt',
      cell: (item) => {
        return <div>{formatDate(item.updatedAt)}</div>;
      },
    },
    {
      header: 'Trạng thái',
      accessorKey: 'status',
      cell: (item) => <StatusBadge status={item.status} />,
    },
    {
      header: 'Chứng từ',
      accessorKey: 'status',
      cell: (item) => renderCertificate(item),
    },
    {
      header: 'Hành động',
      cell: (item) => {
        return (
          <div className="flex items-center gap-2">
            {((checkIsPermisstionApproveLow(profile?.role, item.status) &&
              item.gift.unitPrice *
                item.numberOfGiftsPerCustomer *
                item.customers.length <=
                5000000) ||
              (checkIsPermisstionApproveHigh(profile?.role, item.status) &&
                item.gift.unitPrice *
                  item.numberOfGiftsPerCustomer *
                  item.customers.length >
                  5000000)) && (
              <ApproveButton giftRequest={item} text="phê duyệt" />
            )}
            {((checkIsPermisstionApproveLow(profile?.role, item.status) &&
              item.gift.unitPrice *
                item.numberOfGiftsPerCustomer *
                item.customers.length <=
                5000000) ||
              (checkIsPermisstionApproveHigh(profile?.role, item.status) &&
                item.gift.unitPrice *
                  item.numberOfGiftsPerCustomer *
                  item.customers.length >
                  5000000)) && (
              <ApproveButton giftRequest={item} text="từ chối" />
            )}
            {checkIsPermissionWithScreen(
              'gifts-requests',
              'update',
              profile?.role?.permissions
            ) &&
              checkIsRoleEditGiftRequests(item.status) && (
                <CreateUpdate
                  giftRequest={item}
                  giftsData={giftsData?.gifts || []}
                  customersData={customersData || []}
                  employeesData={employeesData || []}
                  refetchGifts={refetchGifts}
                />
              )}
            {checkIsPermissionWithScreen(
              'gifts-requests',
              'delete',
              profile?.role?.permissions
            ) &&
              item.status === GiftRequestStatus.NEW && (
                <Delete
                  giftRequest={item}
                  setPage={() => {
                    if (
                      data?.giftRequests?.length === 1 &&
                      page === data?.pages
                    ) {
                      setPage(page - 1);
                    }
                  }}
                />
              )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="luxury-theme min-h-screen">
      <div className="flex items-center justify-between bg-white p-4 border-b border-luxury-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="text-2xl  text-luxury-900">
            <TitlePage />
          </div>
        </div>
        {checkIsPermissionWithScreen(
          'gifts-requests',
          'create',
          profile?.role?.permissions
        ) && (
          <CreateUpdate
            giftsData={giftsData?.gifts || []}
            customersData={customersData || []}
            employeesData={employeesData || []}
            refetchGifts={refetchGifts}
          />
        )}
      </div>

      <div className="flex items-center justify-end bg-white p-4 border-b border-luxury-200 shadow-sm gap-4">
        <Select
          onValueChange={(value) => {
            setCustomerId(value);
            setPage(1);
          }}
          value={customerId || ''}
        >
          <SelectTrigger className="w-[250px] luxury-input">
            <SelectValue placeholder="Chọn khách hàng" />
          </SelectTrigger>
          <SelectContent className="border-luxury-200">
            {customersData?.map((customer) => (
              <SelectItem key={customer.id} value={customer.id}>
                KH-{customer.id} - {customer.fullName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative">
          <Select
            onValueChange={(value) => {
              setStatus(value as GiftRequestStatus);
              setPage(1);
            }}
            value={status || ''}
          >
            <SelectTrigger className="w-[250px] luxury-input">
              <SelectValue placeholder="Chọn trạng thái" />
            </SelectTrigger>
            <SelectContent className="border-luxury-200">
              {Object.values(GiftRequestStatus).map((status) => {
                const statusMap = {
                  [GiftRequestStatus.NEW]: 'Mới',
                  [GiftRequestStatus.PENDING]: 'Chờ duyệt',
                  [GiftRequestStatus.BUSINESS_APPROVED]: 'Quản lý đã duyệt',
                  [GiftRequestStatus.ADMIN_APPROVED]: 'Admin đã duyệt',
                  [GiftRequestStatus.GENERAL_DIRECTOR_APPROVED]:
                    'Giám đốc đã duyệt',
                  [GiftRequestStatus.BUSINESS_REJECTED]: 'Quản lý đã từ chối',
                  [GiftRequestStatus.ADMIN_REJECTED]: 'Admin đã từ chối',
                  [GiftRequestStatus.GENERAL_DIRECTOR_REJECTED]:
                    'Giám đốc đã từ chối',
                  [GiftRequestStatus.COMPLETED]: 'Đã hoàn thành',
                  [GiftRequestStatus.APPROVED]: 'Đã duyệt',
                  [GiftRequestStatus.REJECTED]: 'Đã từ chối',
                };
                return (
                  <SelectItem key={status} value={status}>
                    {statusMap[status]}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="relative">
          <Input
            className="w-[300px] pr-10 luxury-input"
            placeholder="Tìm kiếm mã yêu cầu"
            onChange={handleSearch}
            ref={searchRef}
          />
          <Search className="w-4 h-4 absolute right-4 top-1/2 -translate-y-1/2 text-luxury-500" />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="luxury-button-outline"
            onClick={() => {
              setStatus(null);
              setEmployeeId(null);
              setCustomerId(null);
              setSearch('');
              if (searchRef.current) {
                searchRef.current.value = '';
              }
            }}
          >
            <Filter className="w-4 h-4 mr-2" />
            Xoá lọc
          </Button>
        </div>
      </div>

      <div className="px-6 py-6 relative">
        {isFetching && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10">
            <LoadingCircle />
          </div>
        )}
        <TableComponent<GiftRequestDTO>
          columns={columns}
          data={data?.giftRequests || []}
          pageSize={limit}
          totalPages={data?.pages || 0}
          onPageChange={setPage}
          currentPage={page}
          className="luxury-table"
        />
      </div>
    </div>
  );
};

export default ManageGiftRequests;
